// Core Libraries
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Custom Hooks and Utilities
import useForm from "../../hooks/useForm";
import { useThunk } from "../../hooks/useThunk";
import showToast from "../../utils/toastNotifications";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import { roles } from "../../constants/commonConstants";

// Redux Actions/Thunks
import { signin, startDataRefresh } from "../../store";

// Components
import CustomInput from "../Form/Input";
import CustomButton from "../Form/CustomButton";
import CustomCheckbox from "../Form/CustomCheckbox";
import AuthHeader from "./AuthHeader";
import AuthFooter from "./AuthFooter";

const SignInForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { role } = useSelector((state) => state.auth);
  const [doSignin, isSigningIn, signinError] = useThunk(signin);
  const [loginMessage, setLoginMessage] = useState({ type: "", message: "" }); // Add state for login messages
  
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    clearError,
  } = useForm(
    {
      email: "",
      password: "",
      rememberMe: false,
    },
    {
      email: true,
      password: true,
      rememberMe: false,
    },
    {
      email: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      password: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      rememberMe: COMMON_FIELD_TYPES.BOOLEAN_FIELD,
    }
  );
  const [showPassword, setShowPassword] = useState(false);

 const handleSubmit = async (e) => {
  // This is the most important line - it prevents default form submission
  e.preventDefault();

  setLoginMessage({ type: "", message: "" });

  setTouched({
    email: true,
    password: true,
    rememberMe: true,
  });

  const { rememberMe, ...otherData } = formData;
  if ( validateForm()) {
    try {
      // Call the sign-in function
      const result = await doSignin(otherData);
      
      // Check if we have a successful result
      if (result?.success) {
        const token = result?.response?.data?.token;
        localStorage.setItem("token", token);

        showToast("success", "Login Successful");
        setTimeout(() => {
          navigate("/");
        }, 500);
      } else {
        console.error("Login failed:", result?.error);
        setLoginMessage({
          type: "error",
          message: result?.error?.message || "Login failed. Please try again.",
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      setLoginMessage({
        type: "error",
        message: "An unexpected error occurred. Please try again.",
      });
    }
  } else {
    setLoginMessage({
      type: "error",
      message: "Please Login",
    });
  }
};

  return (
    <div className="flex flex-col items-center w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl pt-4 sm:pt-6 md:pt-8 lg:pt-10 pb-3 sm:pb-4 md:pb-5">
      {/* Logo */}
      <AuthHeader />

      {/* Form Container */}
      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-10 pb-6 sm:pb-8 md:pb-10 rounded-lg shadow-lg bg-[#1A1924] font-public-sans font-bold text-sm sm:text-base md:text-lg leading-none tracking-normal text-center">
        {/* Error Message */}
        <div className="h-6 sm:h-7 md:h-8 my-1 sm:my-2 flex items-center justify-center">
          {loginMessage.message && loginMessage.type === "error" && (
            <div className="w-full px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium bg-red-100 text-red-800 border border-red-200 text-center">
              {loginMessage.message}
            </div>
          )}
        </div>

        <h1 className="mb-6 sm:mb-8 md:mb-10 text-xl md:text-[1.625rem] font-bold text-[#B3B3B3]">
          Login to your Account
        </h1>

        <div className="space-y-5 w-full max-w-[18rem] sm:max-w-[20rem] md:max-w-[22rem] mx-auto mb-[20px]">
          {/* Email Field */}
          <div className="relative mb-[20px]">
            
            <CustomInput
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={() => handleBlur("email", formData.email)}
              onFocus={() => {
                clearError("email");
                setLoginMessage({ type: "", message: "" });
              }}
              placeholder="Enter your email"
              error={false}
              isRequired={true}
              className="h-9 sm:h-10 md:h-11 w-full text-white"
              prefixIcon={
                <svg
                  width="16"
                  height="16"
                  className="sm:w-5 sm:h-5"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 0C11.3261 0 12.5979 0.526784 13.5355 1.46447C14.4732 2.40215 15 3.67392 15 5C15 6.32608 14.4732 7.59785 13.5355 8.53553C12.5979 9.47322 11.3261 10 10 10C8.67392 10 7.40215 9.47322 6.46447 8.53553C5.52678 7.59785 5 6.32608 5 5C5 3.67392 5.52678 2.40215 6.46447 1.46447C7.40215 0.526784 8.67392 0 10 0ZM10 12.5C15.525 12.5 20 14.7375 20 17.5V20H0V17.5C0 14.7375 4.475 12.5 10 12.5Z"
                    fill="white"
                  />
                </svg>
              }
            />
          </div>

          <div className="relative mb-[20px]">
            <CustomInput
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              onBlur={() => handleBlur("password", formData.password)}
              onFocus={() => {
                clearError("password");
                setLoginMessage({ type: "", message: "" });
              }}
              placeholder="Enter your password"
              error={false}
              isRequired={true}
              className="h-9 sm:h-10 md:h-11 w-full text-white"
              prefixIcon={
                <svg
                  width="16"
                  height="20"
                  className="sm:w-5 sm:h-6"
                  viewBox="0 0 20 27"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.5 26.5C1.8125 26.5 1.22417 26.2554 0.735 25.7663C0.245833 25.2771 0.000833333 24.6883 0 24V11.5C0 10.8125 0.245 10.2242 0.735 9.735C1.225 9.24583 1.81333 9.00083 2.5 9H3.75V6.5C3.75 4.77083 4.35958 3.29708 5.57875 2.07875C6.79792 0.860418 8.27166 0.250834 10 0.250001C11.7283 0.249168 13.2025 0.858751 14.4225 2.07875C15.6425 3.29875 16.2517 4.7725 16.25 6.5V9H17.5C18.1875 9 18.7762 9.245 19.2662 9.735C19.7562 10.225 20.0008 10.8133 20 11.5V24C20 24.6875 19.7554 25.2763 19.2662 25.7663C18.7771 26.2563 18.1883 26.5008 17.5 26.5H2.5ZM10 20.25C10.6875 20.25 11.2762 20.0054 11.7662 19.5163C12.2562 19.0271 12.5008 18.4383 12.5 17.75C12.4992 17.0617 12.2546 16.4733 11.7662 15.985C11.2779 15.4967 10.6892 15.2517 10 15.25C9.31083 15.2483 8.7225 15.4933 8.235 15.985C7.7475 16.4767 7.5025 17.065 7.5 17.75C7.4975 18.435 7.7425 19.0238 8.235 19.5163C8.7275 20.0088 9.31583 20.2533 10 20.25ZM6.25 9H13.75V6.5C13.75 5.45833 13.3854 4.57292 12.6562 3.84375C11.9271 3.11458 11.0417 2.75 10 2.75C8.95833 2.75 8.07292 3.11458 7.34375 3.84375C6.61458 4.57292 6.25 5.45833 6.25 6.5V9Z"
                    fill="#FEFEFE"
                  />
                </svg>
              }
              icon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      className="sm:w-6 sm:h-6"
                      viewBox="0 0 27 27"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.5 16.875C15.364 16.875 16.875 15.364 16.875 13.5C16.875 11.636 15.364 10.125 13.5 10.125C11.636 10.125 10.125 11.636 10.125 13.5C10.125 15.364 11.636 16.875 13.5 16.875Z"
                        fill="white"
                      />
                      <path
                        d="M20.8206 7.44504C18.515 5.86406 16.0476 5.0625 13.4873 5.0625C11.1834 5.0625 8.93742 5.74805 6.8117 7.09172C4.66805 8.44963 2.56658 10.916 0.84375 13.5C2.23699 15.8203 4.14281 18.206 6.12773 19.5739C8.4048 21.1423 10.8807 21.9375 13.4873 21.9375C16.0713 21.9375 18.5419 21.1428 20.8332 19.5755C22.8503 18.1934 24.7699 15.8108 26.1562 13.5C24.7651 11.2097 22.8393 8.82984 20.8206 7.44504ZM13.5 18.5625C12.4987 18.5625 11.5199 18.2656 10.6874 17.7093C9.8549 17.153 9.20603 16.3624 8.82286 15.4373C8.43969 14.5123 8.33944 13.4944 8.53477 12.5124C8.73011 11.5303 9.21227 10.6283 9.92027 9.92027C10.6283 9.21227 11.5303 8.73011 12.5124 8.53477C13.4944 8.33944 14.5123 8.43969 15.4373 8.82286C16.3624 9.20603 17.153 9.8549 17.7093 10.6874C18.2656 11.5199 18.5625 12.4987 18.5625 13.5C18.561 14.8422 18.0271 16.129 17.078 17.078C16.129 18.0271 14.8422 18.561 13.5 18.5625Z"
                        fill="white"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      className="sm:w-6 sm:h-6"
                      viewBox="0 0 27 27"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.3087 10.125L16.875 13.68V13.5C16.875 12.6049 16.5194 11.7464 15.8865 11.1135C15.2535 10.4806 14.3951 10.125 13.5 10.125H13.3087ZM8.47125 11.025L10.215 12.7687C10.1587 13.005 10.125 13.2412 10.125 13.5C10.125 14.3951 10.4806 15.2535 11.1135 15.8865C11.7464 16.5194 12.6049 16.875 13.5 16.875C13.7475 16.875 13.995 16.8412 14.2312 16.785L15.975 18.5287C15.2212 18.9 14.3887 19.125 13.5 19.125C12.0082 19.125 10.5774 18.5324 9.52252 17.4775C8.46763 16.4226 7.875 14.9918 7.875 13.5C7.875 12.6112 8.1 11.7787 8.47125 11.025ZM2.25 4.80375L4.815 7.36875L5.32125 7.875C3.465 9.3375 2.0025 11.25 1.125 13.5C3.07125 18.4387 7.875 21.9375 13.5 21.9375C15.2437 21.9375 16.9087 21.6 18.4275 20.9925L18.9112 21.465L22.1962 24.75L23.625 23.3212L3.67875 3.375M13.5 7.875C14.9918 7.875 16.4226 8.46763 17.4775 9.52252C18.5324 10.5774 19.125 12.0082 19.125 13.5C19.125 14.22 18.9787 14.9175 18.72 15.5475L22.0162 18.8437C23.7037 17.4375 25.0537 15.5925 25.875 13.5C23.9287 8.56125 19.125 5.0625 13.5 5.0625C11.925 5.0625 10.4175 5.34375 9 5.85L11.4412 8.26875C12.0825 8.02125 12.7687 7.875 13.5 7.875Z"
                        fill="#C8C8C8"
                      />
                    </svg>
                  )}
                </button>
              }
            />
          </div>
          
          {/* Stay signed in checkbox */}
          <CustomCheckbox
            id="rememberMe"
            name="rememberMe"
            label="Stay signed in"
            checked={formData.rememberMe}
            onChange={(value) => handleInputChange("rememberMe", value)}
            className="text-xs sm:text-sm mt-5"
          />

          {/* Login Button */}
          <CustomButton
            buttonText="Login"
            loading={isSigningIn}
            onClick={handleSubmit}
            disabled={isSigningIn}
            className="mt-3 sm:mt-4 w-full h-9 sm:h-10 md:h-11 text-base sm:text-lg md:text-xl font-bold text-[#1E1E1E]"
          />

          {/* Forgot Password Link */}
          <Link
            to="/forgot-password"
            className="font-normal text-[12px] sm:text-base md:text-lg text-[#8D8D8D] font-public-sans underline mt-3 sm:mt-4 md:mt-5 block"
          >
            Forgot Password?
          </Link>
        </div>
      </div>

      {/* Footer */}
      <AuthFooter />
    </div>
  );
};

export default SignInForm;